/**
 * Authentication module (cookie-based BFF).
 *
 * The browser holds no tokens. Login posts credentials to the auth-gateway,
 * which sets an HttpOnly `__Host-session` cookie and keeps the Keycloak tokens
 * server-side. Every subsequent request authenticates from that cookie
 * (sent automatically by the browser); the gateway attaches Bearer auth
 * server-side and refreshes the access token on its own.
 *
 * Flows:
 * - Login: POST /auth/login with email + password. Returns the user identity,
 *   or an MFA challenge if the user has TOTP/SMS configured.
 * - MFA: POST /auth/login with email + password + mfa_code.
 * - Signup: POST /auth/signup with email, password, name. The backend
 *   validates the email domain against the cpty's whitelist.
 * - Auto-login: ?user=email&pass=password query params trigger login
 *   via /auth/login (dev/test only).
 * - Session restore: on page load, GET /auth/me returns the user identity
 *   for the current cookie, or 204 No Content if there is no live session.
 */

/** In-memory user identity for the current session, or null. */
let _userInfo = null;

/**
 * Consent gate, read from /auth/me alongside the identity. While `pending` is
 * true the gateway refuses every UI request, so the app must show the terms
 * rather than boot. `version` is the one the user is being asked to accept, and
 * is shown so the acceptance is against the document they actually read.
 */
let _terms = { pending: false, version: null, effectiveDate: null };

/** Map the gateway's snake_case user payload to the shape the SPA uses. */
function mapUser(user) {
    if (!user) return null;
    return {
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        cptyCode: user.cpty_code ? user.cpty_code.toUpperCase() : null,
        username: user.username,
        role: user.role || "trader",
    };
}

/**
 * Initialize auth: handle auto-login params, otherwise restore the session
 * from the cookie via /auth/me. Returns true if a live session exists.
 */
export async function initAuth() {
    const params = new URLSearchParams(window.location.search);

    // Auto-login: ?user=email&pass=password logs in via /auth/login.
    const autoUser = params.get("user");
    const autoPass = params.get("pass");
    if (autoUser && autoPass) {
        try {
            const resp = await fetch("/auth/login", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: autoUser, password: autoPass }),
            });
            if (resp.ok) {
                const data = await resp.json();
                // Consent gate, carried on the login response as well as
                // /auth/me: a fresh sign-in never goes through the restore
                // probe, so without this the app boots and every request 403s.
                _terms = {
                    pending: !!data.terms_pending,
                    version: data.terms_version || null,
                    effectiveDate: data.terms_effective_date || null,
                };
                _userInfo = mapUser(data.user);
                // If a role param is present, store it so the SPA
                // switches to that role after login completes.
                // Map a friendly role to the internal ROLE enum value.
                // borrower -> borrowing, lender -> lending; the enum values
                // pass through unchanged.
                const autoRole = params.get("role");
                if (autoRole) {
                    const roleMap = {
                        borrower: "borrowing",
                        lender: "lending",
                        borrowing: "borrowing",
                        lending: "lending",
                    };
                    const mappedRole = roleMap[autoRole.toLowerCase()];
                    if (mappedRole) {
                        localStorage.setItem("topbar.set_role", mappedRole);
                    }
                }
                // Map a friendly page to the internal VIEWS enum value so the
                // SPA opens that page after login.
                const autoPage = params.get("page");
                if (autoPage) {
                    const pageMap = {
                        negotiations: "negotiations",
                        transactions: "negotiations",
                        inventory: "inventory",
                        availability: "availability",
                        confirmations: "confirmations",
                    };
                    const mappedPage = pageMap[autoPage.toLowerCase()];
                    if (mappedPage) {
                        localStorage.setItem("topbar.set_view", mappedPage);
                    }
                }
                // Clean up the URL
                const url = new URL(window.location.href);
                url.searchParams.delete("user");
                url.searchParams.delete("pass");
                url.searchParams.delete("role");
                url.searchParams.delete("page");
                window.history.replaceState({}, document.title, url.pathname + url.hash);
                return _userInfo !== null;
            }
            console.error("Auto-login failed:", resp.status);
        } catch (e) {
            console.error("Auto-login error:", e);
        }
    }

    // Restore an existing session from the cookie. 204 means "no session" -
    // the normal signed-out answer (bodyless), not an error.
    try {
        const resp = await fetch("/auth/me", { credentials: "include" });
        if (resp.status === 204) {
            _userInfo = null;
            return false;
        }
        if (resp.ok) {
            const data = await resp.json();
            // Hard MFA enforcement (#33): a pending-MFA session (enforced cpty, no
            // enrolled TOTP) is NOT usable for the app - its requests would 403 and
            // crash the boot. Treat it as signed-out here so the sign-in page shows;
            // re-login drives the mandatory TOTP enrollment.
            if (data && data.mfa_pending) {
                _userInfo = null;
                return false;
            }
            // Consent gate: unlike mfa_pending above, this session IS usable -
            // the user must be signed in to accept. Keep the identity and let
            // the app render the terms instead of booting.
            _terms = {
                pending: !!(data && data.terms_pending),
                version: (data && data.terms_version) || null,
                effectiveDate: (data && data.terms_effective_date) || null,
            };
            _userInfo = mapUser(data);
            return _userInfo !== null;
        }
    } catch (e) {
        console.error("Session restore error:", e);
    }

    _userInfo = null;
    return false;
}

/**
 * Login via /auth/login. Returns an object describing the result:
 *   { success: true, mfa_configured: bool }  - login complete
 *   { mfa_required: true, mfa_type: "totp"|"sms" } - need MFA code
 *
 * Throws on authentication failure.
 */
export async function directLogin(email, password) {
    const resp = await fetch("/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
    });

    const data = await resp.json();

    if (!resp.ok) {
        const err = new Error(data.error || "Login failed");
        err.errorCode = data.error_code;
        throw err;
    }

    // MFA challenge - frontend resends credentials with the code
    if (data.mfa_required) {
        return {
            mfa_required: true,
            mfa_type: data.mfa_type,
            mfa_session: data.mfa_session,
        };
    }

    // Login complete - the cookie is set; keep the identity in memory.
    // Consent gate, carried on the login response as well as /auth/me: a fresh
    // sign-in never goes through the restore probe, so without this the app
    // would boot and every request would 403.
    _terms = {
        pending: !!data.terms_pending,
        version: data.terms_version || null,
        effectiveDate: data.terms_effective_date || null,
    };
    _userInfo = mapUser(data.user);

    return {
        success: true,
        mfa_configured: data.mfa_configured,
        mfa_enforced: data.mfa_enforced,
        // Hard MFA enforcement (#33): the session is restricted to TOTP enrollment
        // until the user enrols. The app must NOT boot (proxied calls 403); the
        // caller sends the user straight into TOTP setup.
        mfa_pending: data.mfa_pending,
    };
}

/**
 * Complete MFA login with a TOTP or SMS code.
 * Resends email + password along with the code (stateless MFA - no
 * server-side credential storage).
 */
export async function verifyMfaCode(email, password, mfaCode) {
    const resp = await fetch("/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, mfa_code: mfaCode }),
    });

    const data = await resp.json();

    if (!resp.ok) {
        const err = new Error(data.error || "Verification failed");
        err.errorCode = data.error_code;
        throw err;
    }

    // Consent gate, carried on the login response as well as /auth/me: a fresh
    // sign-in never goes through the restore probe, so without this the app
    // would boot and every request would 403.
    _terms = {
        pending: !!data.terms_pending,
        version: data.terms_version || null,
        effectiveDate: data.terms_effective_date || null,
    };
    _userInfo = mapUser(data.user);

    return { success: true, mfa_configured: true };
}

/**
 * Register a new user via /auth/signup.
 * The backend validates the email domain against the cpty whitelist.
 */
export async function signUp({ email, password, givenName, familyName }) {
    const resp = await fetch("/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            email,
            password,
            firstName: givenName,
            lastName: familyName,
        }),
    });

    if (resp.status === 409) {
        throw new Error("A user with this email already exists");
    }
    if (resp.status === 403) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || "Email domain not allowed for this counterparty");
    }
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || "Registration failed");
    }
}

/**
 * Request a password-reset email via /auth/forgot-password. The gateway emails a
 * link back to this SPA. Resolves regardless of whether the account exists (the
 * backend never reveals that), so the caller can always show "check your email".
 */
export async function forgotPassword(email) {
    const resp = await fetch("/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
    });
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || "We couldn't send the reset email. Please try again.");
    }
}

/**
 * Set a new password from a reset token (carried in the emailed link) via
 * /auth/reset-password. The token is single-use and deleted on success.
 */
export async function resetPassword(email, token, newPassword) {
    const resp = await fetch("/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, newPassword }),
    });
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || "We couldn't reset your password. Please try again.");
    }
}

/**
 * Log out - the gateway deletes the server session, revokes the Keycloak
 * tokens, and clears the cookie.
 */
export async function logout() {
    try {
        await fetch("/auth/logout", { method: "POST", credentials: "include" });
    } catch (e) {
        // The session is cleared client-side regardless; the cookie is
        // HttpOnly so the browser drops it once the gateway expires it.
        console.warn("Logout request failed:", e);
    }
    _userInfo = null;
}

/**
 * The current user's identity, or null when not logged in. Populated by
 * login / MFA verification / session restore - no JWT decoding.
 */
export function getUserInfo() {
    return _userInfo;
}

/**
 * Re-read the consent state from /auth/me.
 *
 * The version a session was told at sign-in goes stale the moment new terms are
 * published, so anything reacting to a refusal has to ask again rather than
 * show the version the user signed in with.
 */
export async function refreshTermsState() {
    try {
        const resp = await fetch("/auth/me", { credentials: "include" });
        if (!resp.ok) return _terms;
        const data = await resp.json();
        _terms = {
            pending: !!(data && data.terms_pending),
            version: (data && data.terms_version) || null,
            effectiveDate: (data && data.terms_effective_date) || null,
        };
    } catch (e) {
        console.info("[Terms] could not refresh the version", e);
    }
    return _terms;
}

/** The consent gate for the current session: `{ pending, version, effectiveDate }`. */
export function getTermsState() {
    return _terms;
}

/**
 * Record that the user accepts the current terms. The gateway takes the
 * identity from the session and the version from its own constant, so this
 * sends nothing. Resolves true once the gate is open.
 */
export async function acceptTerms() {
    const resp = await fetch("/auth/terms/accept", {
        method: "POST",
        credentials: "include",
    });
    if (!resp.ok) return false;
    _terms = { ..._terms, pending: false };
    return true;
}
